import { RlMediaStream } from "./RlMediaStream";

export class RlAudioStream extends RlMediaStream {
    /** @internal */
    private _lastState: boolean = false;
    private _analyser: AnalyserNode | undefined;
    private _animationFrame: number | undefined;
    private _trackEl: HTMLAudioElement | undefined;
    private _audioContext: AudioContext | undefined;
    private _sourceNode: MediaStreamAudioSourceNode | undefined;

    /**
     * minimum voice level (db) for activity detection
     */
    public MinimumVoiceLevel = -40;

    public get Muted() {
        return this._trackEl.muted;
    }

    public set Muted(s: boolean) {
        this._trackEl.muted = s;
    }

    /**
     * Call when incoming voice has changed
     * @param state voice activity status (true = speaking)
     */
    public VoiceActivityStatus: ((state: boolean) => void) | undefined = undefined;

    /**
     * Play received audio in background if you want play it automatically
     */
    public async Play() {
        if (!this._trackEl) {
            this._trackEl = new Audio();
            this._trackEl.srcObject = this.Stream;
            this._trackEl.autoplay = true;
            this._trackEl.muted = false;
            (this._trackEl as any).playsInline = true;
        }

        try {

            await this._trackEl.play();
        } catch (e) {
            console.warn("Autoplay blocked, waiting for user gesture...", e);

            const unlock = async () => {
                try {
                    await this._trackEl!.play();
                    // resolve();
                } catch (err) {
                    console.error("Play still failed after gesture", err);
                    // reject(err);
                } finally {
                    document.removeEventListener("click", unlock);
                    document.removeEventListener("touchend", unlock);
                }
            };

            document.addEventListener("click",  unlock, { once: true });
            document.addEventListener("touchend", unlock, { once: true });

            throw e;        
        }
        this._setupVoiceActivityDetection();
    }

    /**
     * Automatically stops when stream closed from server
     */
    public Stop() {
        if (this._trackEl) {
            this._trackEl.srcObject = null;
            this._trackEl.autoplay = false;
            this._trackEl = undefined;
        }

        if (this._audioContext) {
            this._audioContext.close();
            this._audioContext = undefined;
        }

        cancelAnimationFrame(this._animationFrame || 0);
    }

    /** @internal */
    private _setupVoiceActivityDetection() {
        if (!this.Stream) return;

        this._audioContext = new AudioContext();
        this._sourceNode = this._audioContext.createMediaStreamSource(this.Stream);
        this._analyser = this._audioContext.createAnalyser();
        this._analyser.fftSize = 512;

        this._sourceNode.connect(this._analyser);

        const dataArray = new Uint8Array(this._analyser.frequencyBinCount);

        const checkVolume = () => {
            this._analyser!.getByteTimeDomainData(dataArray);

            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
                const normalized = (dataArray[i] - 128) / 128;
                sum += normalized * normalized;
            }

            const rms = Math.sqrt(sum / dataArray.length);
            // @ts-ignore
            const decibels = 20 * Math.log10(rms);
            const isSpeaking = decibels > -40;

            if (isSpeaking !== this._lastState) {
                this._lastState = isSpeaking;
                if (this.VoiceActivityStatus) {
                    this.VoiceActivityStatus(isSpeaking);
                }
            }

            this._animationFrame = requestAnimationFrame(checkVolume);
        };

        if (this._audioContext.state === "suspended") {
            this._audioContext.resume().then(() => {
                checkVolume();
            });
        } else {
            checkVolume();
        }
    }
}
